// frontend/src/components/NotificationMenu.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Play, UserPlus, MessageCircle, ArrowBigUp, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const NOTIF_ICONS = {
  watching: { Icon: Play,         color: 'text-green-400',  bg: 'bg-green-500/10' },
  follow:   { Icon: UserPlus,     color: 'text-primary',    bg: 'bg-primary/10' },
  reply:    { Icon: MessageCircle,color: 'text-blue-400',   bg: 'bg-blue-500/10' },
  upvote:   { Icon: ArrowBigUp,   color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
};

export default function NotificationMenu() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* not logged in */ }
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.is_read) {
        await axios.post(`${BACKEND_URL}/api/notifications/${notif.notification_id}/read`, {}, { withCredentials: true });
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n =>
          n.notification_id === notif.notification_id ? { ...n, is_read: true } : n
        ));
      }
      setIsOpen(false);
      navigate(notif.link);
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.is_read);
    for (const n of unread) {
      try {
        await axios.post(`${BACKEND_URL}/api/notifications/${n.notification_id}/read`, {}, { withCredentials: true });
      } catch { /* ignore */ }
    }
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-white/5"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1 shadow-lg animate-in zoom-in-50">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-[-60px] sm:right-0 mt-2 w-[300px] sm:w-[340px] bg-card/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 z-50 overflow-hidden animate-in slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-muted-foreground hover:text-white transition-colors p-0.5">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            ) : (
              notifications.map(notif => {
                const typeInfo = NOTIF_ICONS[notif.type] || NOTIF_ICONS.reply;
                const Icon = typeInfo.Icon;
                return (
                  <div
                    key={notif.notification_id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`flex items-start gap-3 px-4 py-3.5 border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/5 transition-colors ${!notif.is_read ? 'bg-primary/3' : ''}`}
                  >
                    {/* Actor avatar or type icon */}
                    <div className="relative flex-shrink-0">
                      {notif.actor_picture ? (
                        <img src={notif.actor_picture} alt={notif.actor_name}
                          className="w-9 h-9 rounded-full border border-white/10" />
                      ) : (
                        <div className={`w-9 h-9 rounded-full ${typeInfo.bg} flex items-center justify-center`}>
                          <Icon className={`w-4 h-4 ${typeInfo.color}`} />
                        </div>
                      )}
                      <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${typeInfo.bg} border border-card flex items-center justify-center`}>
                        <Icon className={`w-2.5 h-2.5 ${typeInfo.color}`} />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-200 leading-snug">{notif.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                      </p>
                    </div>

                    {/* Poster thumbnail */}
                    {notif.poster_path && (
                      <img src={`https://image.tmdb.org/t/p/w92${notif.poster_path}`} alt=""
                        className="w-8 h-12 rounded object-cover border border-white/10 flex-shrink-0" />
                    )}

                    {!notif.is_read && (
                      <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
